import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell, CartesianGrid } from "recharts";
import { Plus, Trash2 } from "lucide-react";
import ToolShell from "./ToolShell";
import { Panel, NumField, Stat } from "./ToolUI";
import { num, saveResult } from "./toolkit";

const DEFAULT_STATIONS = [
  { name: "Load & clamp", ct: "18", type: "NVA" },
  { name: "Weld", ct: "29", type: "VA" },
  { name: "Deburr", ct: "34", type: "VA" },
  { name: "Inspect", ct: "16", type: "Required" },
  { name: "Pack", ct: "21", type: "Required" },
];
const TYPES = ["VA", "Required", "NVA"];
const inputCls = "w-full border border-input bg-background rounded-sm px-3 h-11 text-sm outline-none focus:border-primary";

const StationRow = ({ s, i, set, remove, over }) => (
  <div className="grid grid-cols-[1fr_1fr_auto] sm:grid-cols-[1fr_90px_110px_auto] gap-2 items-center pb-2 sm:pb-0 border-b sm:border-0 border-border">
    <input placeholder="Station name" aria-label="Station name" value={s.name} onChange={(e) => set(i, "name", e.target.value)} className={`${inputCls} col-span-3 sm:col-span-1`} data-testid={`cycle-station-name-${i}`} />
    <input type="number" min="0" placeholder="CT (s)" aria-label="Cycle time (s)" value={s.ct} onChange={(e) => set(i, "ct", e.target.value)} className={`${inputCls} ${over ? "border-accent text-accent" : ""}`} data-testid={`cycle-station-ct-${i}`} />
    <select aria-label="Step type" value={s.type} onChange={(e) => set(i, "type", e.target.value)} className={inputCls} data-testid={`cycle-station-type-${i}`}>
      {TYPES.map((t) => <option key={t}>{t}</option>)}
    </select>
    <button type="button" onClick={() => remove(i)} className="h-11 px-3 border border-border rounded-sm text-muted-foreground hover:text-red-400 no-print" data-testid={`cycle-station-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
  </div>
);

export default function CycleTimeAnalyzer() {
  const [avail, setAvail] = useState("450");
  const [demand, setDemand] = useState("900");
  const [st, setSt] = useState(DEFAULT_STATIONS);
  const [touched, setTouched] = useState(false);
  const touch = (fn) => (...a) => { setTouched(true); fn(...a); };
  const set = touch((i, k, v) => setSt((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x))));
  const remove = touch((i) => setSt((s) => s.filter((_, j) => j !== i)));

  const r = useMemo(() => {
    const takt = num(demand) ? (num(avail) * 60) / num(demand) : 0;
    const cts = st.map((s) => num(s.ct));
    const sum = cts.reduce((a, b) => a + b, 0);
    const max = Math.max(0, ...cts);
    const bottleneck = st[cts.indexOf(max)];
    const va = st.reduce((a, s) => a + (s.type === "VA" ? num(s.ct) : 0), 0);
    return {
      takt, sum, max, bottleneck,
      balance: st.length && max ? sum / (st.length * max) : 0,
      minOps: takt ? Math.ceil(sum / takt) : 0,
      capacity: max ? Math.floor((num(avail) * 60) / max) : 0,
      vaPct: sum ? va / sum : 0,
      over: st.filter((s) => num(s.ct) > takt).length,
    };
  }, [avail, demand, st]);

  useEffect(() => { if (touched) saveResult("cycle", { balance: r.balance * 100, takt: r.takt }); }, [touched, r.balance, r.takt]);

  const chart = st.map((s) => ({ name: s.name || "—", ct: num(s.ct) }));
  const share = {
    slug: "cycle-time-analyzer", tool: "Line Balance", scoreText: `${(r.balance * 100).toFixed(0)}%`, buttonLabel: "Share my line balance",
    label: `Takt ${r.takt.toFixed(1)}s · bottleneck ${r.max}s · ${st.length} stations`,
    bar: { value: r.balance * 100, target: 85, targetLabel: "Target 85%+" },
    tagline: "Balancing the line to takt with RuleAI",
    caption: `Our line balance efficiency is ${(r.balance * 100).toFixed(0)}% against a ${r.takt.toFixed(1)}s takt. Analyzed free with RuleAI Systems.`,
  };

  return (
    <ToolShell toolKey="cycle" overline="Free tool · Standard work" title="Cycle Time Analyzer"
      intro="Compare each station's cycle time against takt time. Find the bottleneck, see your line balance efficiency, and how many operators the work content really needs."
      share={share} results={{ takt: r.takt, balance: r.balance, minOperators: r.minOps, capacity: r.capacity, stations: st, available: avail, demand }}>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
        <Panel overline="01 / Demand & stations" title="Takt time inputs" testid="cycle-inputs">
          <div className="grid sm:grid-cols-2 gap-4">
            <NumField label="Available time" suffix="min / shift" value={avail} onChange={touch(setAvail)} testid="cycle-available" />
            <NumField label="Customer demand" suffix="units / shift" value={demand} onChange={touch(setDemand)} testid="cycle-demand" />
          </div>
          <div className="mt-6 hidden sm:grid grid-cols-[1fr_90px_110px_auto] gap-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground mb-2">
            <span>Station</span><span>CT (s)</span><span>Type</span><span className="w-10" />
          </div>
          <div className="sm:hidden mt-6 text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground">Each station: name · cycle time (sec) · type (VA / Required / NVA)</div>
          <div className="space-y-2 mt-2 sm:mt-0">
            {st.map((s, i) => <StationRow key={i} s={s} i={i} set={set} remove={remove} over={num(s.ct) > r.takt} />)}
          </div>
          <button type="button" onClick={touch(() => setSt((s) => [...s, { name: `Station ${s.length + 1}`, ct: "20", type: "VA" }]))}
            className="mt-3 text-sm text-primary inline-flex items-center hover:underline no-print" data-testid="cycle-add-station"><Plus className="w-4 h-4 mr-1" />Add station</button>
        </Panel>
        <Panel overline="02 / Results" title="Stations vs. takt" testid="cycle-results">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat label="Takt time" value={`${r.takt.toFixed(1)}s`} testid="cycle-takt" />
            <Stat label="Line balance" value={`${(r.balance * 100).toFixed(1)}%`} tone={r.balance >= 0.85 ? "text-emerald-400" : "text-amber-400"} testid="cycle-balance" />
            <Stat label="Bottleneck" value={`${r.max}s`} sub={r.bottleneck?.name} tone={r.max > r.takt ? "text-accent" : ""} testid="cycle-bottleneck" />
            <Stat label="Min. operators" value={r.minOps} sub={`${r.sum}s work content`} testid="cycle-min-ops" />
            <Stat label="Capacity" value={r.capacity.toLocaleString()} sub={`vs ${num(demand)} demand`} tone={r.capacity < num(demand) ? "text-accent" : "text-emerald-400"} testid="cycle-capacity" />
            <Stat label="Value-added" value={`${(r.vaPct * 100).toFixed(0)}%`} sub="of work content" testid="cycle-va" />
          </div>
          <div className="h-64 mt-5">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid stroke="#27272a" vertical={false} />
                <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Bar dataKey="ct" name="Cycle time (s)">{chart.map((c, i) => <Cell key={i} fill={c.ct > r.takt ? "#ea580c" : "#3b82f6"} />)}</Bar>
                <ReferenceLine y={r.takt} stroke="#fafafa" strokeDasharray="5 4" label={{ value: `Takt ${r.takt.toFixed(1)}s`, fill: "#fafafa", fontSize: 11, position: "insideTopRight" }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-muted-foreground mt-3" data-testid="cycle-insight">
            {r.over ? `${r.over} station(s) exceed takt — the line can't meet demand. Rebalance work from ${r.bottleneck?.name} or remove NVA steps.` : "All stations are within takt. Look for stations far below takt to consolidate and free up operators."}
          </p>
        </Panel>
      </div>
    </ToolShell>
  );
}
